// src/admin/AdminMedia.jsx

import React, { useState, useEffect } from 'react';
import { supabase } from 'src/lib/supabase';
import { useMediaUpload } from '../hooks/useMediaUpload';
import MediaCard from './media/MediaCard';
import MediaDrawer from './media/MediaDrawer';

const mockMediaFiles = [
  {
    id: '1',
    url: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop',
    versions: { 
      thumb: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=300&h=200&fit=crop', 
      medium: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=600&fit=crop', 
      large: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1600&h=1200&fit=crop' 
    },
    type: 'image',
    bucket: 'services',
    size: 1024000,
    width: 1920,
    height: 1080,
    alt_text: 'Professional cleaning service',
    created_at: new Date().toISOString(),
    uploaded_by: 'system'
  },
  {
    id: '2',
    url: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=300&fit=crop',
    versions: { 
      thumb: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=300&h=200&fit=crop', 
      medium: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800&h=600&fit=crop', 
      large: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1600&h=1200&fit=crop' 
    },
    type: 'image',
    bucket: 'jobs',
    size: 2048000,
    width: 1920,
    height: 1080,
    alt_text: 'Plumbing repair job',
    created_at: new Date(Date.now() - 86400000).toISOString(),
    uploaded_by: 'user123'
  },
  {
    id: '3',
    url: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop',
    versions: { 
      thumb: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&h=200&fit=crop', 
      medium: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&h=600&fit=crop' 
    },
    type: 'image',
    bucket: 'avatars',
    size: 512000,
    width: 800,
    height: 800,
    alt_text: 'Professional electrician',
    created_at: new Date(Date.now() - 172800000).toISOString(),
    uploaded_by: 'user456'
  }
];

const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const { upload, uploading, progress, error: uploadError, reset: resetUpload } = useMediaUpload();

  useEffect(() => {
    loadMedia();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

    const loadMedia = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('media')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMedia(data || []);
    } catch (error) {
      console.error('Error loading media:', error);
      setMedia([]);
      showToast('Failed to load media library. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const result = await upload(file, {
      bucket: 'content',
      entityType: 'content',
      entityId: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      altText: file.name
    });
    
    if (result) {
      showToast(`Successfully uploaded ${file.name}`, 'success');
      await loadMedia();
    } else if (uploadError) {
      showToast(uploadError, 'error');
    }
    resetUpload();
    event.target.value = '';
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} item(s)? This action cannot be undone.`)) return;
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const id of selectedIds) {
      const mediaItem = media.find(m => m.id === id);
      if (mediaItem) {
        try {
          // Delete from storage — path/bucket live inside metadata now
          const path = mediaItem.metadata?.path;
          const bucket = mediaItem.metadata?.bucket;
          if (path && bucket) {
            await supabase.storage.from(bucket).remove([path]);
          }
          // Delete from media table (cascades to media_links)
          const { error } = await supabase.from('media').delete().eq('id', id);
          if (error) throw error;
          successCount++;
        } catch (err) {
          console.error('Error deleting media:', err);
          errorCount++;
        }
      }
    }
    
    if (successCount > 0) {
      showToast(`Deleted ${successCount} item(s) successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`, 'success');
    } else if (errorCount > 0) {
      showToast(`Failed to delete ${errorCount} item(s)`, 'error');
    }
    
    setSelectedIds(new Set());
    await loadMedia();
  };

  const getDateFilter = () => {
    const now = new Date();
    switch(dateFilter) {
      case 'today':
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { $gte: today.toISOString() };
      case 'week':
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        return { $gte: weekAgo.toISOString() };
      case 'month':
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        return { $gte: monthAgo.toISOString() };
      default:
        return null;
    }
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.alt_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    const matchesBucket = bucketFilter === 'all' || item.bucket === bucketFilter;
    
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const itemDate = new Date(item.created_at);
      const now = new Date();
      if (dateFilter === 'today') {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        matchesDate = itemDate >= today;
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        matchesDate = itemDate >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        matchesDate = itemDate >= monthAgo;
      }
    }
    
    return matchesSearch && matchesType && matchesBucket && matchesDate;
  });

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === filteredMedia.length && filteredMedia.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMedia.map(m => m.id)));
    }
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          padding: '12px 20px',
          backgroundColor: toast.type === 'error' ? '#DC2626' : toast.type === 'info' ? '#6366F1' : '#059669',
          color: 'white',
          borderRadius: 8,
          fontSize: 14,
          zIndex: 2000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}>
          {toast.message}
        </div>
      )}
      
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, margin: 0, color: '#0F172A' }}>Media Library</h1>
        <p style={{ fontSize: 14, color: '#64748B', marginTop: 8 }}>Manage images and videos across your platform</p>
      </div>
      
      {/* Filters Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by filename or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            height: 40,
            padding: '0 12px',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            fontSize: 14,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            outline: 'none'
          }}
        />
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ height: 40, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", backgroundColor: 'white', cursor: 'pointer' }}
        >
          <option value="all">All Types</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
        </select>
        
        <select
          value={bucketFilter}
          onChange={(e) => setBucketFilter(e.target.value)}
          style={{ height: 40, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", backgroundColor: 'white', cursor: 'pointer' }}
        >
          <option value="all">All Buckets</option>
          <option value="avatars">Avatars</option>
          <option value="jobs">Jobs</option>
          <option value="services">Services</option>
          <option value="content">Content</option>
        </select>
        
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          style={{ height: 40, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", backgroundColor: 'white', cursor: 'pointer' }}
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
        
        <label
          style={{
            height: 40,
            padding: '0 20px',
            backgroundColor: '#0F172A',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1E293B'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0F172A'}
        >
          Upload File
          <input type="file" accept="image/*,video/*" onChange={handleFileUpload} style={{ display: 'none' }} />
        </label>
        
        {selectedIds.size > 0 && (
          <button
            onClick={handleBulkDelete}
            style={{
              height: 40,
              padding: '0 20px',
              backgroundColor: '#DC2626',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#B91C1C'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
          >
            Delete ({selectedIds.size})
          </button>
        )}
      </div>
      
      {/* Upload Progress */}
      {uploading && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: 14, marginBottom: 8, color: '#0F172A' }}>Uploading: {progress}%</div>
          <div style={{ height: 4, backgroundColor: '#E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#6366F1', transition: 'width 0.3s' }} />
          </div>
        </div>
      )}
      
      {/* Select All Bar */}
      {filteredMedia.length > 0 && !loading && (
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', color: '#0F172A' }}>
            <input 
              type="checkbox" 
              checked={selectedIds.size === filteredMedia.length && filteredMedia.length > 0} 
              onChange={selectAll}
              style={{ cursor: 'pointer', width: 16, height: 16 }}
            />
            Select All ({filteredMedia.length})
          </label>
          <span style={{ fontSize: 13, color: '#64748B' }}>
            {selectedIds.size} selected
          </span>
        </div>
      )}
      
      {/* Grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{ borderRadius: 12, backgroundColor: '#F8FAFC', overflow: 'hidden', border: '1px solid #F1F5F9' }}>
              <div style={{ height: 160, backgroundColor: '#E2E8F0', animation: 'pulse 1.5s ease-in-out infinite' }} />
              <div style={{ padding: 12 }}>
                <div style={{ height: 16, backgroundColor: '#E2E8F0', borderRadius: 4, marginBottom: 8, width: '70%' }} />
                <div style={{ height: 12, backgroundColor: '#E2E8F0', borderRadius: 4, width: '40%' }} />
              </div>
            </div>
          ))}
        </div>
      ) : filteredMedia.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 80, backgroundColor: '#F8FAFC', borderRadius: 12, border: '1px solid #F1F5F9' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>📷</div>
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8, color: '#0F172A' }}>No media found</div>
          <div style={{ fontSize: 14, color: '#64748B', marginBottom: 20 }}>Upload your first file to get started</div>
          <label style={{
            padding: '10px 24px',
            backgroundColor: '#6366F1',
            color: 'white',
            borderRadius: 8,
            cursor: 'pointer',
            display: 'inline-block',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: "'Plus Jakarta Sans', sans-serif"
          }}>
            Upload File
            <input type="file" accept="image/*,video/*" onChange={handleFileUpload} style={{ display: 'none' }} />
          </label>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {filteredMedia.map(item => (
            <MediaCard
              key={item.id}
              media={item}
              selected={selectedIds.has(item.id)}
              onSelect={() => toggleSelect(item.id)}
              onClick={() => {
                setSelectedMedia(item);
                setDrawerOpen(true);
              }}
            />
          ))}
        </div>
      )}
      
      {/* Drawer */}
      {drawerOpen && selectedMedia && (
        <MediaDrawer
          media={selectedMedia}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedMedia(null);
          }}
          onUpdate={loadMedia}
          showToast={showToast}
        />
      )}
      
      {/* Add pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default AdminMedia;